import { useOutletContext, Link } from 'react-router';
import { Plus, Package } from 'lucide-react';
import {
  Button,
  UniversalLoadingState,
  UniversalEmptyState,
  Card,
  StatusBadge,
  MoneyCell,
  DateCell,
  NotificationCard,
} from '@sighthost/ui';
import { useServiceAddons, type Service } from '@/domains/hosting';

export default function ServiceAddonsTab() {
  const service = useOutletContext<Service>();
  const { data: addons, isLoading, error } = useServiceAddons(service.id);

  if (isLoading) {
    return <UniversalLoadingState variant="spinner" caption="Loading addons…" />;
  }

  if (error) {
    return <NotificationCard tone="danger" title="Could not load addons" body={error.message} />;
  }

  const addonRequestSubject = `Add an addon — ${service.primaryDomain ?? service.cpanelUsername}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-soft">
          Optional extras subscribed to this service.
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to={`/app/support/new?dept=billing&subject=${encodeURIComponent(addonRequestSubject)}`}>
            <Plus className="h-4 w-4" />
            Request an addon
          </Link>
        </Button>
      </div>

      {addons.length === 0 ? (
        <Card interactive={false}>
          <UniversalEmptyState
            icon={Package}
            title="No addons yet"
            description="Need extra mailboxes, an SSL certificate, or off-server backups? Open a ticket and we'll quote and provision the addon for you."
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {addons.map((addon) => (
            <Card key={addon.id} interactive={false} className="flex items-start justify-between gap-4 p-4">
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-ink">{addon.name}</h4>
                  <StatusBadge tone={addon.status === 'active' ? 'active' : 'draft'}>
                    {addon.status === 'active' ? 'Active' : 'Cancelled'}
                  </StatusBadge>
                </div>
                <p className="text-sm text-foreground-soft">{addon.description}</p>
                <p className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1">
                  Added <DateCell value={addon.addedAt} format="long" />
                </p>
              </div>
              <div className="text-right">
                <MoneyCell value={addon.monthlyPriceZAR} />
                <div className="text-xs text-muted-foreground">/ month</div>
              </div>
            </Card>
          ))}
        </ul>
      )}
    </div>
  );
}
